import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckSquare, ChevronLeft, ChevronRight, Search, Square, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input, Select, Switch } from '@/components/ui/Input';
import { StatusBadge } from '@/components/ui/Badge';
import { fetchEmailAccounts, fetchMailAccountGroups } from '@/lib/api/email-accounts';
import type { EmailAccountItem } from '@/types/api';
import type { MailProviderKey } from '@/pages/campaigns/new/lib/wizard-store';

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const MAX_VISIBLE_CHIPS = 12;

interface Props {
  workspaceId: string;
  selected: string[];
  providers?: MailProviderKey[];
  onChange: (ids: string[]) => void;
}

export function MailAccountsPicker({ workspaceId, selected, providers = [], onChange }: Props) {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterGroup, setFilterGroup] = useState('');
  const [onlyAvailable, setOnlyAvailable] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [showAllSelected, setShowAllSelected] = useState(false);

  const normalizedProviders = useMemo(() => providers, [providers]);
  const serverProvider = normalizedProviders.length === 1 ? normalizedProviders[0] : undefined;
  const serverProviders =
    normalizedProviders.length > 1 ? normalizedProviders.join(',') : undefined;
  const offset = (page - 1) * pageSize;

  useEffect(() => {
    setPage(1);
  }, [filterGroup, onlyAvailable, pageSize, searchQuery, serverProvider, serverProviders]);

  const accountsQuery = useQuery({
    queryKey: [
      'email-accounts',
      workspaceId,
      searchQuery,
      filterGroup,
      onlyAvailable,
      normalizedProviders,
      page,
      pageSize,
    ],
    queryFn: () =>
      fetchEmailAccounts({
        workspace_id: workspaceId,
        provider: serverProvider,
        providers: serverProviders,
        q: searchQuery || undefined,
        group_id: filterGroup || undefined,
        available: onlyAvailable || undefined,
        limit: pageSize,
        offset,
      }),
    staleTime: 30_000,
    enabled: !!workspaceId,
  });

  const groupsQuery = useQuery({
    queryKey: ['mail-account-groups', workspaceId],
    queryFn: () => fetchMailAccountGroups(workspaceId),
    staleTime: 60_000,
    enabled: !!workspaceId,
  });

  const pageAccounts = (accountsQuery.data?.items ?? []) as EmailAccountItem[];
  const totalAccounts = accountsQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalAccounts / pageSize));
  const groups = groupsQuery.data?.groups ?? [];

  const accounts =
    normalizedProviders.length > 1
      ? pageAccounts.filter(
          (account) =>
            (account.provider === 'instantly' || account.provider === 'successai') &&
            normalizedProviders.includes(account.provider),
        )
      : pageAccounts;

  const selectedAccounts = accounts.filter((account) => selected.includes(account.id));
  const unselectedAccounts = accounts.filter((account) => !selected.includes(account.id));
  const visibleAccounts = [...selectedAccounts, ...unselectedAccounts];
  const visibleIds = useMemo(() => visibleAccounts.map((account) => account.id), [visibleAccounts]);
  const visibleSelectedCount = useMemo(
    () => visibleIds.filter((id) => selected.includes(id)).length,
    [selected, visibleIds],
  );
  const allVisibleSelected =
    visibleIds.length > 0 && visibleSelectedCount === visibleIds.length;
  const selectedVisibleAccounts = visibleAccounts.filter((account) => selected.includes(account.id));
  const hiddenSelectedCount = Math.max(0, selected.length - selectedVisibleAccounts.length);
  const selectedChips = showAllSelected ? selected : selected.slice(0, MAX_VISIBLE_CHIPS);

  function toggle(id: string) {
    if (selected.includes(id)) {
      onChange(selected.filter((entry) => entry !== id));
      return;
    }
    onChange([...selected, id]);
  }

  function selectGroup(groupId: string) {
    const group = groups.find((entry) => entry.id === groupId);
    if (!group) return;
    const visibleSet = new Set(group.account_ids);
    const rowsInFilter = new Set(visibleAccounts.map((account) => account.id));
    const toAdd = [...visibleSet].filter((id) => rowsInFilter.has(id) && !selected.includes(id));
    onChange([...selected, ...toAdd]);
  }

  function removeChip(id: string) {
    onChange(selected.filter((entry) => entry !== id));
  }

  function toggleAllVisible() {
    if (allVisibleSelected) {
      const visibleSet = new Set(visibleIds);
      onChange(selected.filter((id) => !visibleSet.has(id)));
      return;
    }
    onChange(Array.from(new Set([...selected, ...visibleIds])));
  }

  return (
    <div className="space-y-4" data-testid="mail-accounts-picker">
      <div className="grid gap-3 xl:grid-cols-[minmax(0,1.3fr)_180px_180px_auto_auto]">
        <label className="relative block">
          <Search
            size={14}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <Input
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            className="pl-9"
            placeholder="Rechercher un email ou un domaine..."
            aria-label="Rechercher un compte expediteur"
            data-testid="account-search"
          />
        </label>

        <Select
          value={filterGroup}
          onChange={(event) => setFilterGroup(event.target.value)}
          className="text-sm"
          aria-label="Filtrer par groupe"
          data-testid="filter-group"
        >
          <option value="">Tous les groupes</option>
          {groups.map((group) => (
            <option key={group.id} value={group.id}>
              {group.name}
            </option>
          ))}
        </Select>

        <Select
          value={String(pageSize)}
          onChange={(event) => setPageSize(Number(event.target.value))}
          className="text-sm"
          aria-label="Taille de page"
          data-testid="page-size"
        >
          {PAGE_SIZE_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option} / page
            </option>
          ))}
        </Select>

        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
          <Switch checked={onlyAvailable} onChange={setOnlyAvailable} />
          Disponibles uniquement
        </label>

        {visibleAccounts.length > 0 && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={toggleAllVisible}
            className="justify-self-start xl:justify-self-end"
            data-testid="toggle-all-visible"
            aria-pressed={allVisibleSelected}
          >
            {allVisibleSelected ? <CheckSquare size={14} /> : <Square size={14} />}
            {allVisibleSelected
              ? `Tout decocher (${visibleSelectedCount})`
              : `Tout cocher (${visibleAccounts.length - visibleSelectedCount} restant${
                  visibleAccounts.length - visibleSelectedCount > 1 ? 's' : ''
                })`}
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-brand border border-brand-border bg-gray-50 px-4 py-3 text-sm text-gray-600">
        {normalizedProviders.length > 0 && (
          <span data-testid="provider-filter-summary">
            <span className="font-medium text-gray-800">Providers:</span>{' '}
            {normalizedProviders.join(' + ')}
          </span>
        )}
        <span>
          <span className="font-medium text-gray-800">{selected.length}</span> compte(s) selectionne(s)
        </span>
        <span>
          <span className="font-medium text-gray-800">{totalAccounts}</span> resultat(s)
        </span>
        {hiddenSelectedCount > 0 && (
          <span className="text-xs text-gray-500">
            {hiddenSelectedCount} selection(s) ne sont pas sur cette page.
          </span>
        )}
      </div>

      {groups.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="group-buttons">
          {groups.map((group) => (
            <Button
              key={group.id}
              variant="secondary"
              size="sm"
              type="button"
              onClick={() => selectGroup(group.id)}
              data-testid={`select-group-${group.id}`}
              disabled={!visibleAccounts.some((account) => group.account_ids.includes(account.id))}
            >
              {group.name} ({group.account_count})
            </Button>
          ))}
        </div>
      )}

      {selected.length > 0 && (
        <div className="rounded-brand border border-brand-border bg-white p-3" data-testid="selected-chips">
          <div className="mb-2 flex items-center justify-between gap-3">
            <p className="text-sm font-medium text-gray-800">Selection courante</p>
            {selected.length > MAX_VISIBLE_CHIPS && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowAllSelected((value) => !value)}
                data-testid="toggle-selected-chip-view"
              >
                {showAllSelected ? 'Voir moins' : `Voir tout (${selected.length})`}
              </Button>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {selectedChips.map((id) => {
              const account = visibleAccounts.find((entry) => entry.id === id);
              return (
                <span
                  key={id}
                  className="inline-flex items-center gap-1 bg-brand-primary-light text-brand-primary text-xs px-2 py-1 rounded-full"
                >
                  {account?.email ?? id}
                  <button
                    type="button"
                    onClick={() => removeChip(id)}
                    className="hover:opacity-75"
                    aria-label={`Retirer ${account?.email ?? id}`}
                  >
                    <X size={10} />
                  </button>
                </span>
              );
            })}
          </div>
        </div>
      )}

      {accountsQuery.isLoading ? (
        <p className="text-sm text-gray-400" data-testid="accounts-loading">
          Chargement...
        </p>
      ) : accountsQuery.isError ? (
        <p className="text-sm text-red-500">Erreur lors du chargement des comptes.</p>
      ) : visibleAccounts.length === 0 ? (
        <p className="text-sm text-gray-400">Aucun compte disponible.</p>
      ) : (
        <>
          <div
            className="rounded-brand border border-brand-border overflow-hidden"
            data-testid="accounts-list"
          >
            <div className="grid grid-cols-[minmax(0,1.4fr)_140px_120px_120px] gap-3 border-b border-brand-border bg-gray-50 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-400">
              <span>Compte</span>
              <span>Provider</span>
              <span>Usage</span>
              <span>Statut</span>
            </div>
            <div className="divide-y divide-brand-border max-h-[34rem] overflow-y-auto">
              {visibleAccounts.map((account) => (
                <AccountRow
                  key={account.id}
                  account={account}
                  selected={selected.includes(account.id)}
                  onToggle={() => toggle(account.id)}
                />
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-3 rounded-brand border border-brand-border bg-gray-50 px-4 py-3 text-sm text-gray-600 md:flex-row md:items-center md:justify-between">
            <p data-testid="accounts-page-summary">
              Page {page} / {totalPages} - {totalAccounts} resultat(s)
            </p>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setPage((value) => Math.max(1, value - 1))}
                disabled={page === 1}
                data-testid="accounts-prev-page"
              >
                <ChevronLeft size={14} />
                Precedent
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
                disabled={page >= totalPages}
                data-testid="accounts-next-page"
              >
                Suivant
                <ChevronRight size={14} />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function AccountRow({
  account,
  selected,
  onToggle,
}: {
  account: EmailAccountItem;
  selected: boolean;
  onToggle: () => void;
}) {
  return (
    <label className="grid cursor-pointer grid-cols-[minmax(0,1.4fr)_140px_120px_120px] items-center gap-3 px-4 py-3 hover:bg-gray-50">
      <div className="flex min-w-0 items-start gap-3">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          className="mt-1 h-4 w-4 rounded border border-brand-border accent-brand-primary"
          aria-label={account.email}
        />
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-gray-800">{account.email}</p>
          <p className="truncate text-xs text-gray-500">{account.domain}</p>
        </div>
      </div>
      <span className="w-fit rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">
        {account.provider}
      </span>
      <span className="text-xs text-gray-500">
        {account.current_daily_sent}/{account.daily_limit} today
      </span>
      <div className="flex justify-start md:justify-end">
        <StatusBadge status={account.status} />
      </div>
    </label>
  );
}
